import React, { Component } from "react";
import StatsBox from "../StatsPage/StatsBox.js";
import InfoTable from "../InfoTable.js";
import ResultInfoText from "../ResultsTable/ResultInfoText.js";
import KingdomIcon from "../ReactionTable/kingdomIcon.js";
import { kingdomLink } from "../../functions/taxonomyLinks.js";


import getDataByEnzymesFromSearchResults from "../../api/transformSearchResultsToEnzymes.js";
import { getUniqueMoleculesFromEnzyme, splitEnzymesByMoleculeRole } from "../../api/HelperFunctions.js";
import { useParams } from "react-router-dom";
import BasicRecordTable from "./BasicRecordTable.js";
import ReactionResultBox from "../ResultsTable/ReactionResultBox.js";
import translateTerpeneType from "../../api/typeTranslator.js";
import { useState } from "react";
import SmallLoading from "../smallLoading.js";
import { useNavigate } from "react-router-dom";
import MoleculesDiv from "../MoleculeTable/MoleculesDiv.js";
import MechanismViewer from "../MechanismView/mechanismViewer.js";
import EnzymeDisplayBox from "../EnzymeDisplayBox.js";

function MoleculeRecord({searchResult, setMechBody, navigateToRecord}) {
    // Declared before the early return below so the hook order stays stable
    // (useNavigate was already imported here but never called).
    const navigate = useNavigate();

    if (!searchResult.reactions){
        return <div className="loading-div"><SmallLoading /></div>;
    }
    console.log("MoleculeRecord searchResult:", searchResult);
    const reactionsData = getDataByEnzymesFromSearchResults(searchResult.reactions);
    // The reaction list now contains every reaction of each enzyme. Derived
    // views ("products from this molecule", the mechanism box, the type/class
    // summary) must stay scoped to the reactions that actually involve this
    // molecule (flagged is_relevant by the backend, covers the intermediate case).
    const relevantReactionsData = reactionsData
        .map(enzyme => ({ ...enzyme, reactions: enzyme.reactions.filter(r => r.is_relevant) }))
        .filter(enzyme => enzyme.reactions.length > 0);
    let molecules = getUniqueMoleculesFromEnzyme(relevantReactionsData);

    const history = searchResult.marts;
    const molecule = searchResult.molecule;
    // Split the touching enzymes by the role this molecule plays for each of
    // them, so the record can list major producers, minor producers, substrate
    // users and (mechanism) intermediate enzymes separately. Computed once here
    // from the already-grouped data — no extra requests.
    const enzymeRoles = splitEnzymesByMoleculeRole(reactionsData, molecule.marts);
    molecules.products = molecules.products.filter(product => product.marts !== molecule.marts);
    molecules.substrates = molecules.substrates.filter(substrate => substrate.marts !== molecule.marts);
    const kingdoms = new Set(reactionsData.map(row => row.kingdom));
    const species = new Set(reactionsData.map(row => row.species));
    
    const speciesCountPerKingdom = Array.from(kingdoms).reduce((acc, kingdom) => {
        acc[kingdom] = new Set(
            reactionsData
                .filter(row => row.kingdom === kingdom)
                .map(row => row.species)
        ).size;
        return acc;
    }, {});
    const typeCounts = {};
    const classCounts = {};



    let foundReaction = false;
    let hasMechanism = false;

    relevantReactionsData.forEach(row => {
        row.reactions.forEach(reaction => {
            if (reaction.mechanism_marts) {
                hasMechanism = true;
            }
            if (reaction.product.marts === molecule.marts) {
                foundReaction = true;
                typeCounts[reaction.type] = (typeCounts[reaction.type] || 0) + 1;
                classCounts[reaction.class] = (classCounts[reaction.class] || 0) + 1;
            }
        });
    });

    if (!foundReaction) {
        relevantReactionsData.forEach(row => {
            row.reactions.forEach(reaction => {
                typeCounts[reaction.type] = (typeCounts[reaction.type] || 0) + 1;
                classCounts[reaction.class] = (classCounts[reaction.class] || 0) + 1;
            });
        });
    }

    const mostCommonType = Object.keys(typeCounts).reduce((a, b) => typeCounts[a] > typeCounts[b] ? a : b, null);
    const type_string = translateTerpeneType(mostCommonType) + (molecule.smiles.includes("+") ? " intermediate carbocation" : "");
    const mostCommonClass = Object.keys(classCounts).reduce((a, b) => classCounts[a] > classCounts[b] ? a : b, null);
    console.log("MoleculeRecord reactionsData:", reactionsData);
    return (
        <>
        <StatsBox title={
            <><ResultInfoText text={molecule.name} row={1} fontSize="25px" buttonClass="plane-text-button-no-background"/>  {" · "}
            <ResultInfoText text={molecule.marts} row={1} fontSize="20px" buttonClass="plane-text-button-no-background"/></>}>
            <img src={`/mol_${molecule.molecule_id}.png`} alt={molecule.name} class="wrapped-image" loading="lazy" decoding="async"></img>
            <BasicRecordTable rows={["SMILES", "Formula","Molecular mass", "Type", "Chebi", "Kingdoms", "Enzymes", "InChI", "InChIKey"]} 
            records={[molecule.smiles + (molecule.stereochemistry ? ` (${molecule.stereochemistry} stereochemistry)` : ""), molecule.formula,molecule.molmass, type_string,
             (molecule.chebi ? <ResultInfoText text={molecule.chebi} row={2} linkText={`Find in ChEBI: ${molecule.chebi}`}
           link={`https://www.ebi.ac.uk/chebi/searchId.do?chebiId=CHEBI:${molecule.chebi}`} buttonClass="plane-text-button-no-background"/> : "Missing in chebi"),
           [...kingdoms].sort().map((kingdom, index) => (<KingdomIcon table={kingdoms} row={index-25} isQuickSearch={true} interactiveSearch={() => {}} backgroundColor="rgb(255,255,255, 0)" kingdomVal={kingdom} speciesVal={` in ${speciesCountPerKingdom[kingdom]} species`} onKingdomClick={(clicked) => navigate(kingdomLink(clicked))}/>))
           , reactionsData.length, molecule.inchi, molecule.inchikey ]} />
        </StatsBox>
        <StatsBox title = "Reactions and enzymes">
            {molecules.products.length > 0 &&
            (<StatsBox title = {<ResultInfoText text={"Products from this molecule"} row={1} buttonClass="plane-text-button-no-background" tooltipText={`Displays all molecules that are made from ${molecule.marts} in the order of the number of reactions in MARTS they appear in.`}/>}>
                <MoleculesDiv molecules={molecules.products} startingDisplay={15} displayStep={5} />
            </StatsBox>)}
        {hasMechanism && (
            <StatsBox title="Mechanism">
                <MechanismViewer  searchData={{molecules: [molecule.marts,], getMechanismsOfSubstratesAndProducts: true} } orientation={ molecules.products.length >5 ? "TD" : "LR" } height="1000px"/>
            </StatsBox>
        )}
        <div style={{ display: "flex", justifyContent: "center" }}>Found {reactionsData.length} enzyme{reactionsData.length === 1 ? "" : "s"}</div>
        {[
            { list: enzymeRoles.majorProducers, title: `Enzymes producing ${molecule.name} as a major product`},
            { list: enzymeRoles.minorProducers, title: `Enzymes producing ${molecule.name} as a minor product`},
            { list: enzymeRoles.substrateUsers, title: `Enzymes using ${molecule.name} as a substrate`},
            { list: enzymeRoles.intermediates, title: `Enzymes with ${molecule.name} as a reaction intermediate`},
        ].map(({ list, title, tooltip }) => list.length > 0 && (
            <StatsBox key={title} title={<ResultInfoText text={`${title} (${list.length})`} row={1} buttonClass="plane-text-button-no-background" tooltipText={tooltip} />}>
                <EnzymeDisplayBox
                    reactionsDataByEnzymes={list}
                    setMechBody={setMechBody}
                    navigateToRecord={navigateToRecord}
                    isQuickSearch={true}
                    interactiveSearch={() => {}}
                    startingDisplay={10}
                    displayStep={10}
                />
            </StatsBox>
        ))}
        </StatsBox>
        
        <StatsBox title="Record history">
            <InfoTable data={[["Date"].concat(history.map(row => row.note_date.split("T")[0])), ["Record"].concat(history.map(row => row.note))]} />
        </StatsBox>
        </ >
    );

}

export default MoleculeRecord;